import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WriteAnimationEvents } from '../types';
import { elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';

@customElement('gz-text-animation')
export class GzTextAnimationWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  message = '';

  @property({ type: Number, attribute: true, reflect: true })
  timeoutMs = 1;

  @property({ type: Boolean, attribute: true, reflect: true })
  hideAnimation = false;

  @query('.message')
  private messageElement: HTMLElement;

  private hideBlinkingCursorClass = 'hide-blinking-cursor';

  static get styles() {
    return styles;
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('message')) {
      this.updateComplete.then(() => this.typeMessage());
    }
  }

  render(): TemplateResult {
    return html` <div class="message"></div> `;
  }

  typeMessage(): void {
    if (!this.message) {
      return;
    }

    this.animateTyping().then(() => {
      this.sleep(this.timeoutMs).then(() => {
        // let the cursor animation be displayed for some seconds after typing has finished, then hide it
        elementClassListAdd(this.messageElement, this.hideBlinkingCursorClass);
      });
      this.dispatchEvent(
        new CustomEvent(WriteAnimationEvents.FINISHED, {
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  async animateTyping(): Promise<void> {
    this.resetMessage();

    this.dispatchEvent(
      new CustomEvent(WriteAnimationEvents.STARTED, {
        bubbles: true,
        composed: true,
      })
    );

    elementClassListAdd(this.messageElement, this.hideBlinkingCursorClass);
    if (this.hideAnimation) {
      await this.formatMessage(this.message);
      return;
    }
    elementClassListRemove(this.messageElement, this.hideBlinkingCursorClass);

    for (let i = 0; i < this.message.length; i++) {
      for (const character of this.message[i]) {
        this.messageElement.innerHTML += character;
        await this.sleep(this.timeoutMs);
      }
    }

    await this.formatMessage(this.message);
  }

  private async formatMessage(message: string) {
    this.resetMessage();
    const boldRegex = /\*\*(.*?)\*\*/g;
    const codeRegex = /`(.*?)`/g;
    message = message.replace(boldRegex, '<b>$1</b>');
    message = message.replace(codeRegex, '<code>$1</code>');
    this.messageElement.innerHTML = message;
  }

  private resetMessage(): void {
    this.messageElement.innerHTML = '';
  }

  private sleep = (ms: number | undefined) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-text-animation': GzTextAnimationWebComponent;
  }
}
